import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeftIcon,
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import {
  getMyAttendanceCorrections,
  submitAttendanceCorrection,
   
} from "../services/employee.service";

/* -------------------------------------------------------
   FONTS (GLOBAL POPPINS + ROBOTO)
-------------------------------------------------------- */
const FontLoader = () => (
  <link
    rel="stylesheet"
    href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto:wght@300;400;500;700&display=swap"
  />
);

/* -------------------------------------------------------
   CORRECTION TYPES
-------------------------------------------------------- */
const CORRECTION_TYPES = [
  { id: "missing_punch", label: "Missing Punch" },
  { id: "wrong_punch", label: "Wrong Punch" },
  { id: "half_day", label: "Half-Day Correction" },
  { id: "work_from_home", label: "Work From Home Adjustment" },
];

/* -------------------------------------------------------
   STATUS BADGE
-------------------------------------------------------- */
const StatusBadge = ({ status }) => {
  const cls =
    status === "Approved"
      ? "bg-green-100 text-green-700 border-green-300"
      : status === "Declined"
      ? "bg-red-100 text-red-700 border-red-300"
      : "bg-yellow-100 text-yellow-700 border-yellow-300";

  return (
    <span
      className={`px-2 py-1 rounded-full border text-xs font-semibold tracking-wide ${cls}`}
    >
      {status}
    </span>
  );
};

/* -------------------------------------------------------
   MAIN PAGE: Attendance Correction Request
-------------------------------------------------------- */
const AttendanceCorrectionRequest = () => {
  const [form, setForm] = useState({
    date: "",
    type: "missing_punch",
    inTime: "",
    outTime: "",
    reason: "",
  });

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getMyAttendanceCorrections();

      console.log("FULL RESPONSE DATA:", data);

      const rows = Array.isArray(data) ? data : [];

      console.log("🧾 CORRECTIONS ROWS:", rows);

      const mapped = rows.map(row => ({
        id: row[0],
        emp_code: row[1],
        attendance_date: row[2],
        correction_type: row[3],
        corrected_in: row[4],
        corrected_out: row[5],
        reason: row[10],
        status:
          row[11] === "APPROVED"
            ? "Approved"
            : row[11] === "REJECTED"
            ? "Declined"
            : "Pending",
        created_at: row[12],
      }));

      setRequests(mapped);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch correction requests. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const calculateHours = (inTime, outTime) => {
    if (!inTime || !outTime) return "00:00";

    const [iH, iM] = inTime.split(":").map(Number);
    const [oH, oM] = outTime.split(":").map(Number);

    let totalMin = oH * 60 + oM - (iH * 60 + iM);
    if (totalMin < 0) return "00:00";

    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;

    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const correctedHours = useMemo(
    () => calculateHours(form.inTime, form.outTime),
    [form.inTime, form.outTime]
  );

  const submitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const CORRECTION_TYPE_API_MAP = {
      missing_punch: "MISSING_PUNCH",
      wrong_punch: "WRONG_PUNCH",
      half_day: "HALF_DAY",
      work_from_home: "WFH",
    };

    let payload = {
      attendance_date: form.date,
      correction_type: CORRECTION_TYPE_API_MAP[form.type],
      reason: form.reason.trim(),
    };

    // 🔥 Always send corrected times when user enters them
    if (form.inTime) {
      payload.corrected_in = form.inTime;
    }

    if (form.outTime) {
      payload.corrected_out = form.outTime;
    }

    try {
      await submitAttendanceCorrection(payload);

      setForm({
        date: "",
        type: "missing_punch",
        inTime: "",
        outTime: "",
        reason: "",
      });

      await fetchRequests();
    } catch (err) {
      setError("Failed to submit request. Please check your inputs.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <FontLoader />

      <div className="max-w-7xl mx-auto p-6 font-roboto">
        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-poppins font-semibold text-gray-800 flex items-center">
            <ClockIcon className="h-8 w-8 text-indigo-600 mr-3" />
            Attendance Correction Request
          </h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* ============================== */}
          {/* LEFT: FORM PANEL              */}
          {/* ============================== */}
          <div className="bg-white shadow-xl p-6 rounded-2xl border">
            <h3 className="text-lg font-poppins font-semibold mb-4">
              Submit a Correction
            </h3>

            <form className="space-y-5" onSubmit={submitRequest}>
              <div>
                <label className="text-sm font-medium">Date*</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) =>
                    setForm({ ...form, date: e.target.value })
                  }
                  required
                  className="w-full mt-1 p-2 border rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Correction Type*</label>
                <select
                  value={form.type}
                  onChange={(e) =>
                    setForm({ ...form, type: e.target.value })
                  }
                  className="w-full mt-1 p-2 border rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {CORRECTION_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Correct In-Time</label>
                  <input
                    type="time"
                    value={form.inTime}
                    onChange={(e) =>
                      setForm({ ...form, inTime: e.target.value })
                    }
                    required={form.type !== "half_day" && form.type !== "work_from_home"}
                    className="w-full mt-1 p-2 border rounded-lg"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">
                    Correct Out-Time
                  </label>
                  <input
                    type="time"
                    value={form.outTime}
                    onChange={(e) =>
                      setForm({ ...form, outTime: e.target.value })
                    }
                    required={form.type !== "half_day" && form.type !== "work_from_home"}
                    className="w-full mt-1 p-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="text-sm text-gray-600">
                <p className="font-medium">Corrected Hours:</p>
                <p className="text-lg font-poppins text-indigo-600">
                  {correctedHours}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium">Reason*</label>
                <textarea
                  rows="3"
                  value={form.reason}
                  onChange={(e) =>
                    setForm({ ...form, reason: e.target.value })
                  }
                  className="w-full mt-1 p-2 border rounded-lg"
                  placeholder="Explain why correction is required"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className={`w-full bg-indigo-600 text-white p-3 rounded-xl font-semibold hover:bg-indigo-700 transition shadow ${
                  submitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {submitting ? "Submitting..." : "Submit Request"}
              </button>
            </form>
          </div>

          {/* ============================== */}
          {/* RIGHT: REQUEST LIST        */}
          {/* ============================== */}
          <div className="lg:col-span-2">
            <h3 className="text-xl font-poppins font-semibold mb-4">
              Recent Requests
            </h3>

            <div className="bg-white rounded-2xl shadow-xl border overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Corrected Hours</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Reason</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-10 text-center text-gray-500">
                        Loading requests...
                      </td>
                    </tr>
                  ) : requests.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-10 text-center text-gray-500">
                        No correction requests found.
                      </td>
                    </tr>
                  ) : (
                    requests.map((req, idx) => (
                      <tr
                        key={req.id || idx}
                        className="border-t hover:bg-gray-50 transition"
                      >
                        <td className="px-4 py-3 text-sm">{req.attendance_date}</td>
                        <td className="px-4 py-3 text-sm">
                          {CORRECTION_TYPES.find(
                            t => t.id.toUpperCase() === req.correction_type
                          )?.label || req.correction_type}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {req.corrected_in && req.corrected_out
                            ? `${req.corrected_in.slice(0, 5)} - ${req.corrected_out.slice(0, 5)}`
                            : req.corrected_in
                            ? req.corrected_in.slice(0, 5)
                            : req.corrected_out
                            ? req.corrected_out.slice(0, 5)
                            : "--"}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={req.status} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {req.reason}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceCorrectionRequest;